﻿import { Component, For, Show, createResource, createSignal, createEffect, on, onCleanup } from 'solid-js';
import { thenWait, range } from '../../Utils';
import { LocationData, LocationCard } from './LocationCard';

interface LocationsResponse {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    data: LocationData[];
}

interface GetLocationsData {
    page: number;
    pageSize: number;
    query: string;
    currentPosition: CurrentPosition|null;
}

export interface CurrentPosition {
    lat: number;
    lng: number;
}

async function getLocations(data: GetLocationsData): Promise<LocationsResponse> {
    const { page, pageSize, query, currentPosition } = data;

    const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
    });

    if (currentPosition) {
        params.set('lat', String(currentPosition.lat));
        params.set('lng', String(currentPosition.lng));
    } else if (query) {
        params.set('query', query);
    }

    const fetcher = fetch(`/umbraco/api/locations/search?${params}`, {
        method: 'get',
        headers: {
            'Accept': 'application/json',
        },
    }).then(res => {
        if (res.ok) {
            return res.json();
        }

        return Promise.reject(new Error(`Failed to get Locations: ${res.status} ${res.statusText}`));
    });

    return await thenWait(2000, fetcher);
}

interface LocationsResultsProps {
    query: string;
    currentPosition: CurrentPosition|null;
    pageSize: number;
}

export const LocationsResults: Component<LocationsResultsProps> = (props) => {
    const [scrollPosition, setScrollPosition] = createSignal(0);
    const [ page, setPage ] = createSignal(1);
    const [ locations ] = createResource(() => {
        return {
            page: page(),
            pageSize: props.pageSize,
            query: props.query,
            currentPosition: props.currentPosition,
        };
    }, getLocations);

    // createEffect for handling automate scroll when nextPage or prevPage is triggered
    createEffect(() => {
        const handleScroll = () => {
          setScrollPosition(window.pageYOffset);
        };
      
        window.addEventListener('scroll', handleScroll);
      
        onCleanup(() => {
          window.removeEventListener('scroll', handleScroll);
        });
      });

      function scrollToTop() {
          const isMobileView = window.innerWidth < 768; // Adjust the breakpoint as needed

        const top = isMobileView ? 1300 : 900; // Adjust the top value for mobile and non-mobile views
        window.scrollTo({
          top: top,
          behavior: 'smooth',
        });
      }

    // Reset page if props update
    createEffect(on(
        () => [ props.query, props.currentPosition ],
        () => setPage(1),
    ));

    function onChange(e: Event) {
        const target = e.currentTarget as HTMLSelectElement;
        const pageIndex = Number.parseInt(target.value);

        setPage(pageIndex);
    }

    function nextPage() {
        const totalPages = locations()?.totalPages ?? 0;
      
        setPage((p) => {
          const nextPage = p >= totalPages ? totalPages : p + 1;
          scrollToTop();
          return nextPage;
        });
      }
      
      function prevPage() {
        setPage((p) => {
          const prevPage = p <= 1 ? 1 : p - 1;
          scrollToTop();
          return prevPage;
        });
      }

    return (<>
        <Show when={locations.loading}>
            <div class="absolute inset-0 flex items-center bg-black/50 z-overlay">
                <svg class="block self-center justify-self-center animate-spinner-spin mx-auto text-white w-40 h-40">
                    <circle class="animate-spinner-dash"
                            cx="50%"
                            cy="50%"
                            r="20"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="4"
                            stroke-linecap="round"/>
                </svg>
            </div>
        </Show>
        <Show when={locations.latest != null}>
            <div class="grid w-full gap-8 md:grid-cols-2 xl:grid-cols-3">
                <For each={locations.latest?.data} fallback={<h4 class="col-span-2 text-m-h3 lg:text-h3">Inga områden hittades för din sökning</h4>}>
                    {(location) => (<LocationCard location={location} />)}
                </For>
            </div>
        </Show>
        <Show when={locations.latest?.totalPages}>
            <div class="flex items-center gap-2">
                <button onClick={prevPage}
                        disabled={page() <= 1}
                        class="p-1 bg-secondary-blue text-white rounded-full hover:bg-primary active:bg-secondary-blue active:shadow-sm disabled:bg-grayscale-400 disabled:cursor-not-allowed">
                    <svg xmlns="http://www.w3.org/2000/svg"
                         viewBox="0 0 24 24"
                         fill="none"
                         role="img"
                         aria-hidden="true"
                         class="inline-block h-6 w-6 lg:h-8 lg:w-8">
                        <path d="M20 12H5m0 0 6-5m-6 5 6 6"
                              stroke="currentColor"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"/>
                    </svg>
                </button>
                <select onChange={onChange}
                        class="border-secondary-blue rounded">
                    <For each={range(locations()?.totalPages ?? 0, 1)}>
                        {(p) => (
                            <option value={p}
                                    disabled={locations()?.page == p}
                                    selected={locations()?.page == p}>
                                {p} av {locations()?.totalPages}
                            </option>
                        )}
                    </For>
                </select>
                <button disabled={page() >= (locations()?.totalPages ?? 0)}
                        onClick={nextPage}
                        class="p-1 bg-secondary-blue text-white rounded-full hover:bg-primary active:bg-secondary-blue active:shadow-sm disabled:bg-grayscale-400 disabled:cursor-not-allowed">
                    <svg xmlns="http://www.w3.org/2000/svg"
                         viewBox="0 0 24 24"
                         fill="none"
                         role="img"
                         aria-hidden="true"
                         class="inline-block h-6 w-6 lg:h-8 lg:w-8">
                        <path d="M5 12h15m-6-5 6 5-6 6"
                              stroke="currentColor"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"/>
                    </svg>
                </button>
            </div>
        </Show>
    </>);
}

export default LocationsResults;
